import { css } from '@emotion/react';
import { Typography, MenuItem, Button, Link } from '@mui/material';
import { useCommunityContext, useGlobalState } from 'providers';
import { Fragment } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';

import { UserAffiliations } from '@jebel/constants';

import { CommunitySelect } from 'features/community';
import {
  RouteLayout,
  Box,
  Form,
  FormTextField,
  FormSelect,
  FormPhoneField,
} from 'shared/components/ui';
import { ROUTES, FIELDS_CONFIG_USER } from 'shared/constants';
import { useToast, useUserRequestAccess } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

const requestAccessCSS = css`
  display: flex;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  flex-direction: column;
  height: 100%;
  padding-bottom: 30px;
`;

const topBoxCSS = css`
  display: grid;
  grid-row-gap: 35px;
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
`;

const formBoxCSS = css`
  display: grid;
  grid-gap: 20px;
`;

const namesBoxCSS = theme => css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${theme.spacing(2.5)}px;

  ${theme.breakpoints.down('xs')} {
    grid-template-columns: 1fr;
  }
`;

const buttonsBoxCSS = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const haveAccountCSS = css`
  text-align: center;
  white-space: nowrap;
`;

type FormData = {
  affiliation?: string;
  affiliationDescription?: string;
  email?: string;
  firstName?: string;
  graduatingYear?: string;
  lastName?: string;
  phoneNumber?: string;
};

export function RequestAccessPage() {
  const { push: navigate } = useHistory();
  const { helpModal, toasts } = useGlobalState();
  const { sendUserRequest } = useUserRequestAccess({ shouldRedirect: true });
  const { showError } = useToast();

  const {
    selectedCommunity: community,
    loading: isCommunitiesLoading,
    selecting: isSelectingCommunity,
  } = useCommunityContext();

  const onSubmit = async (form: FormData) => {
    if (!community) {
      showError(
        `Please check the URL you are using to access this page, we didn't find a community associated with it.`,
        { reportable: false },
      );

      return navigate(ROUTES.public.login);
    }

    try {
      const email = form.email as string;

      const response = await sendUserRequest({
        affiliation: form.affiliation as string,
        affiliationDescription: form.affiliationDescription,
        email: email,
        firstname: form.firstName as string,
        lastname: form.lastName as string,
        graduatingYear: form.graduatingYear,
        phoneNumber: form.phoneNumber,
        school: { id: community?.id },
      });

      const message =
        response.data?.commit?.message ??
        `Thank you for registering to ${community.name} community! Your request to join the community has been submitted and is currently under review. You will receive an email notification once your request has been processed.`;

      toasts.showSuccess(message);
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        toasts.showError(err.message);
      }
    }
  };

  const handleOpenNeedHelp = () => {
    helpModal.open({ subject: 'Need help creating my account' });
  };

  return (
    <RouteLayout css={requestAccessCSS}>
      <Box css={topBoxCSS}>
        <Form<FormData> oldCss={formBoxCSS} onSubmit={onSubmit}>
          {({ values, isSubmitting }) => {
            const disableSubmit = isSubmitting || isCommunitiesLoading || isSelectingCommunity;

            return (
              <Fragment>
                <CommunitySelect disabled={!!community} required />

                <Box css={namesBoxCSS}>
                  <FormTextField name="firstName" label="First Name" required />
                  <FormTextField name="lastName" label="Last Name" required />
                </Box>

                <FormTextField name="email" label="Email Address" required />

                <FormPhoneField name="phoneNumber" />

                <FormSelect name="affiliation" label="Affiliation" required>
                  {FIELDS_CONFIG_USER.affiliation.map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </FormSelect>

                {values.affiliation === UserAffiliations.Alumni && (
                  <FormSelect name="graduatingYear" label="Graduation Year" required>
                    {FIELDS_CONFIG_USER.graduatingYear.map(item => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </FormSelect>
                )}

                {values.affiliation === UserAffiliations.Other && (
                  <FormTextField
                    name="affiliationDescription"
                    label="Affiliation Description"
                    placeholder="e.g. Director"
                    helperText="Type a brief description of your affiliation with the school"
                    required
                  />
                )}

                <Box css={buttonsBoxCSS}>
                  <Button
                    disabled={disableSubmit}
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                  >
                    {isSubmitting ? 'Requesting Access...' : 'Request Access'}
                  </Button>

                  <Typography css={haveAccountCSS}>
                    <span>If you already have an account, &nbsp;</span>

                    <Link component={RouterLink} color="secondary" to={ROUTES.public.login}>
                      Login here
                    </Link>

                    <span>.</span>
                  </Typography>

                  <Link color="secondary" align="center" onClick={handleOpenNeedHelp}>
                    Need Help?
                  </Link>
                </Box>
              </Fragment>
            );
          }}
        </Form>
      </Box>
    </RouteLayout>
  );
}
